import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQ {
    question: string;
    answer: string;
}

const FAQS: FAQ[] = [
    {
        question: 'What is a Murrah buffalo and how can I identify it?',
        answer:
            'The Murrah buffalo is one of the best dairy buffalo breeds in India, well known for its high milk yield, strong genetics, and adaptability to different climatic conditions.\n\n' +
            'Identification features of Murrah Buffalo:\n' +
            '• Jet-black skin color\n' +
            '• Short, tightly curved horns (spiral shape)\n' +
            '• Broad head with a prominent forehead\n' +
            '• Well-developed udder\n' +
            '• Strong and compact body structure\n' +
            '• High milk-producing capacity',
    },
    {
        question: 'How can I identify a female Murrah buffalo in India?',
        answer:
            'A female Murrah buffalo can be identified using the following characteristics:\n\n' +
            '• Deep black skin without patches\n' +
            '• Medium to large body size\n' +
            '• Small, thick, and tightly curved horns\n' +
            '• Well-shaped udder with evenly placed teats\n' +
            '• Calm temperament and good feeding response\n' +
            '• High milk yield potential\n\n' +
            'All female Murrah buffaloes provided through Animal Kart are carefully selected and verified by experts.',
    },
    {
        question:
            'After purchasing, how does delivery work and why is there a 6-month gap for the next buffalo unit?',
        answer:
            'When you purchase 1 unit, you receive:\n\n' +
            '• 2 Murrah buffaloes\n' +
            '• 2 calves\n\n' +
            'Delivery cycle explanation:\n' +
            '• 1 Murrah buffalo + 1 calf will be transported to the farm within 1 month\n' +
            '• The remaining 1 Murrah buffalo + 1 calf will be imported and delivered within 6 months\n\n' +
            'This structured cycle ensures:\n' +
            '• Continuous monthly revenue\n' +
            '• Proper quarantine and health monitoring\n' +
            '• Reduced operational risk\n' +
            '• Better long-term profitability\n\n' +
            'This delivery cycle is designed to maximize income stability and overall profit.',
    },
    {
        question: 'Why is CPF (Cattle Protection Fund) mandatory?',
        answer:
            'CPF is mandatory to ensure complete protection and long-term security of your livestock investment.\n\n' +
            'CPF benefits include:\n' +
            '• Full medical care in case of health issues\n' +
            '• Replacement of buffalo in case of critical loss\n' +
            '• Artificial insemination support to ensure the birth of female Murrah calves only\n' +
            '• Continuous health monitoring\n' +
            '• Live CCTV access to monitor your buffaloes in real time\n\n' +
            'CPF safeguards your asset and ensures uninterrupted income generation.',
    },
    {
        question: 'Why do I need to purchase at least 1 unit?',
        answer:
            '1 unit includes:\n' +
            '• 2 Murrah buffaloes\n' +
            '• 2 calves\n\n' +
            'Purchasing 1 full unit is mandatory because:\n' +
            '• It ensures higher and more stable income\n' +
            '• It supports proper milk production cycles\n' +
            '• It enables eligibility for CPF benefits\n' +
            '• It provides long-term profit scalability\n\n' +
            'Purchasing only 1 buffalo + 1 calf does not generate sufficient income or profit and is therefore not supported.',
    },
    {
        question: 'What is Animal Kart?',
        answer:
            'Animal Kart is a digital platform that allows you to purchase units of high-quality livestock and earn returns through professional management.',
    },
    {
        question: 'How do I purchase a unit?',
        answer:
            'Browse the buffalo list, select a breed, choose the number of units, and complete the payment using Online or Manual payment options.',
    },
    {
        question: 'What is CPF?',
        answer:
            'CPF (Cattle Protection Fund) is an protection for your investment, covering risks associated with livestock health.',
    },
    {
        question: 'How does the referral program work?',
        answer:
            'Once you purchase your first unit, your referral feature is unlocked. You can then refer friends by entering their contact details in the "Refer & Earn" section.',
    },
    {
        question: 'What rewards do I get for referrals?',
        answer:
            'You earn coins for every unit your friend purchases (excluding CPF). These coins can be accumulated to unlock more units.',
    },
    {
        question: 'Are the referral rewards cumulative?',
        answer:
            'Yes! If your friend purchases multiple units at once or over time, you receive the 5% reward for each unit they buy.',
    },
    {
        question: 'How can I track my referrals?',
        answer:
            'You can track all your successful referrals and earned coins in the "Refer & Earn" section under "Track my referrals".',
    },
    {
        question: 'What are the payment options?',
        answer:
            'We support Manual payment (Direct Bank Transfer).',

    },
    {
        question: 'Can I sell or transfer my units?',
        answer:
            'Yes, units can be transferred or sold within the platform according to the terms of service after a minimum holding period.',
    },
    {
        question: 'How do I contact support?',
        answer:
            'If you have any issues, you can "Raise a Request" in the HelpDesk, or use our WhatsApp support for quick assistance.',
    },
];

const FAQItem: React.FC<{ item: FAQ }> = ({ item }) => {
    const [isOpen, setIsOpen] = useState(false);

    const renderContent = (text: string) => {
        const lines = text.split('\n');
        const elements: React.ReactNode[] = [];
        let listItems: string[] = [];
        let keyCounter = 0;

        const flushList = () => {
            if (listItems.length > 0) {
                elements.push(
                    <ul key={`list-${keyCounter++}`} className="pl-5 mb-4 list-disc space-y-1 text-gray-600">
                        {listItems.map((li, idx) => (
                            <li key={idx}>
                                {li.substring(1).trim()}
                            </li>
                        ))}
                    </ul>
                );
                listItems = [];
            }
        };

        lines.forEach((line) => {
            const trimmed = line.trim();
            if (trimmed.startsWith('•')) {
                listItems.push(trimmed);
            } else {
                flushList();
                if (trimmed) {
                    elements.push(
                        <p key={`p-${keyCounter++}`} className="mb-4 text-gray-600 leading-relaxed">
                            {trimmed}
                        </p>
                    );
                }
            }
        });
        flushList();

        return elements;
    };

    return (
        <div
            className={`bg-white rounded-2xl overflow-hidden transition-all duration-300 border border-slate-100 ${isOpen ? 'shadow-lg shadow-slate-100 mb-6' : 'hover:shadow-md hover:shadow-slate-100 mb-3'}`}
        >
            <div
                className="flex justify-between items-center p-5 cursor-pointer bg-white hover:bg-slate-50 transition-colors gap-4"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="font-bold text-[#1E293B] text-lg tracking-tight">{item.question}</span>
                {isOpen ? <ChevronUp className="text-indigo-600 flex-shrink-0" size={24} /> : <ChevronDown className="text-slate-400 flex-shrink-0" size={24} />}
            </div>

            <div
                className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
            >
                <div className="p-6 pt-2 border-t border-slate-100 bg-slate-50/30">
                    {renderContent(item.answer)}
                </div>
            </div>
        </div>
    );
};

const Support: React.FC = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateField = (name: string, value: string) => {
        let error = '';
        if (name === 'firstName' || name === 'lastName') {
            if (value.trim().length === 0) {
                error = 'This field is required';
            } else if (/\d/.test(value)) {

                error = 'Name should not contain numbers';
            }
        } else if (name === 'email') {
            if (value.trim().length > 0) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) error = 'Please enter a valid email address';
            }
        } else if (name === 'phone') {
            const phoneRegex = /^\d{10}$/;
            if (!phoneRegex.test(value)) error = 'Please enter exactly 10 digits';
        } else if (name === 'message') {
            if (value.trim().length === 0) error = 'Issue description is required';
        }

        return error;
    };

    const isFormValid = formData.firstName.trim().length > 0 &&
        !/\d/.test(formData.firstName) &&
        formData.lastName.trim().length > 0 &&
        !/\d/.test(formData.lastName) &&

        /^\d{10}$/.test(formData.phone) &&
        formData.message.trim().length > 0 &&
        (formData.email.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) &&
        Object.keys(errors).length === 0;


    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        if (error) {
            setErrors(prev => ({ ...prev, [name]: error }));
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        let processedValue = value;
        // Special handling for phone to allow only digits and max 10 characters
        if (name === 'phone') {
            processedValue = value.replace(/\D/g, '').slice(0, 10);
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        // Validate immediately if there was already an error, 
        // or if it's a field where we want instant feedback
        const error = validateField(name, processedValue);
        if (error) {
            // Only show instant error if they've already tried to leave the field once 
            // or if the value is becoming "more invalid"?
            // Actually, let's just clear errors if valid, and set errors if they were already there.
            if (errors[name]) {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };


    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validate all fields
        const newErrors: Record<string, string> = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key as keyof typeof formData]);
            if (error) newErrors[key] = error;
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);
        console.log('Support form submitted:', formData);

        // Simulate API call
        setTimeout(() => {
            alert('Thank you for contacting support! We will get back to you shortly.');
            setIsSubmitting(false);
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                message: ''
            });
            setErrors({});
        }, 1000);
    };

    return (
        <div className="animate-in fade-in duration-500">
            <div className="mb-12">
                <h1 className="text-3xl font-bold text-[#1E293B] mb-4 tracking-tight uppercase">Contact Support</h1>
                <p className="text-base text-[#64748B] font-medium leading-relaxed max-w-2xl">We are here to help. Check our FAQs below or fill out the form to get in touch with our team.</p>
            </div>

            <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

                {/* FAQ Section */}
                <div className="w-full">
                    <h2 className="text-xl font-bold text-[#1E293B] mb-8 flex items-center gap-3 uppercase tracking-tight">
                        <span className="w-1.5 h-6 bg-indigo-600 rounded-full"></span>
                        Frequently Asked Questions
                    </h2>
                    <div className="space-y-4">
                        {FAQS.map((faq, index) => (
                            <FAQItem key={index} item={faq} />
                        ))}
                    </div>
                </div>

                {/* Contact Form Section */}
                <div className="bg-white rounded-[1.5rem] shadow-sm border border-slate-200 p-8 animate-in slide-in-from-right-4 duration-700">
                    <h2 className="text-xl font-bold text-[#1E293B] mb-8 uppercase tracking-tight">Send us a message</h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <label htmlFor="firstName" className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider pl-1">First Name</label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    className={`w-full px-4 py-3 rounded-xl border ${errors.firstName ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-indigo-500'} focus:ring-2 ${errors.firstName ? 'focus:ring-red-500/10' : 'focus:ring-indigo-500/10'} transition-all outline-none bg-slate-50/30 focus:bg-white text-sm font-semibold text-[#1E293B]`}
                                    placeholder="John"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                />
                                {errors.firstName && <p className="text-[10px] text-red-500 font-bold mt-1 pl-1 uppercase tracking-tight">{errors.firstName}</p>}
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="lastName" className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider pl-1">Last Name</label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    className={`w-full px-4 py-3 rounded-xl border ${errors.lastName ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-indigo-500'} focus:ring-2 ${errors.lastName ? 'focus:ring-red-500/10' : 'focus:ring-indigo-500/10'} transition-all outline-none bg-slate-50/30 focus:bg-white text-sm font-semibold text-[#1E293B]`}
                                    placeholder="Doe"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                />
                                {errors.lastName && <p className="text-[10px] text-red-500 font-bold mt-1 pl-1 uppercase tracking-tight">{errors.lastName}</p>}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="email" className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider pl-1">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className={`w-full px-4 py-3 rounded-xl border ${errors.email ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-indigo-500'} focus:ring-2 ${errors.email ? 'focus:ring-red-500/10' : 'focus:ring-indigo-500/10'} transition-all outline-none bg-slate-50/30 focus:bg-white text-sm font-semibold text-[#1E293B]`}
                                placeholder="john@example.com (Optional)"
                                value={formData.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            {errors.email && <p className="text-[10px] text-red-500 font-bold mt-1 pl-1 uppercase tracking-tight">{errors.email}</p>}
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="phone" className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider pl-1">Phone Number</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                className={`w-full px-4 py-3 rounded-xl border ${errors.phone ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-indigo-500'} focus:ring-2 ${errors.phone ? 'focus:ring-red-500/10' : 'focus:ring-indigo-500/10'} transition-all outline-none bg-slate-50/30 focus:bg-white text-sm font-semibold text-[#1E293B]`}
                                placeholder="9876543210"
                                value={formData.phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                maxLength={10}
                                required
                            />
                            {errors.phone && <p className="text-[10px] text-red-500 font-bold mt-1 pl-1 uppercase tracking-tight">{errors.phone}</p>}
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="message" className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider pl-1">How can we help you?</label>
                            <textarea
                                id="message"
                                name="message"
                                rows={4}
                                className={`w-full px-4 py-3 rounded-xl border ${errors.message ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-indigo-500'} focus:ring-2 ${errors.message ? 'focus:ring-red-500/10' : 'focus:ring-indigo-500/10'} transition-all outline-none bg-slate-50/30 focus:bg-white resize-none text-sm font-semibold text-[#1E293B]`}
                                placeholder="Describe your issue or question..."
                                value={formData.message}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                            ></textarea>
                            {errors.message && <p className="text-[10px] text-red-500 font-bold mt-1 pl-1 uppercase tracking-tight">{errors.message}</p>}
                        </div>


                        <button
                            type="submit"
                            disabled={!isFormValid || isSubmitting}
                            className={`w-full ${(!isFormValid || isSubmitting) ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none' : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-100 hover:shadow-xl hover:shadow-indigo-200 transform hover:-translate-y-0.5'} font-bold py-4 rounded-xl transition-all text-xs uppercase tracking-widest mt-4 flex items-center justify-center gap-2`}
                        >
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Support;
